import React, { useState } from "react";
import { Select, Button } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { fetchProjectUsers } from "../services/projectsAPI";
import { filterOption } from "../utils/textUtils";
import { useAuth } from "../hooks/useAuth";
import CreateUserModal from "./CreateUserModal";

const renderCreateUserFooter = (menu, onCreateClick) => (
  <>
    {menu}
    <div
      style={{
        borderTop: "1px solid #f0f0f0",
        padding: "8px",
      }}
    >
      <Button
        type="link"
        icon={<PlusOutlined />}
        onMouseDown={(event) => event.preventDefault()}
        onClick={onCreateClick}
        style={{ padding: 0, width: "100%", textAlign: "left" }}
      >
        Create User
      </Button>
    </div>
  </>
);

const ProjectUserSelect = ({
  value,
  onChange,
  mode = "multiple",
  placeholder = "Select users",
  disabled = false,
  className,
  allowCreateUser = true,
  allowClear = false,
  roles = null,
  searchValue: controlledSearchValue,
  onSearch: controlledOnSearch,
  ...rest
}) => {
  const { selectedProject } = useAuth();
  const projectId = selectedProject?.project_id;
  const userRole = selectedProject?.role;
  const canCreateUser =
    allowCreateUser && (userRole === "owner" || userRole === "project_manager");
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [internalSearchValue, setInternalSearchValue] = useState("");

  const searchValue =
    controlledSearchValue !== undefined
      ? controlledSearchValue
      : internalSearchValue;
  const onSearch = controlledOnSearch || setInternalSearchValue;

  // Keep the active filter after a multi-select choice so users can select
  // several people from the same company/search result. Single selects still
  // need to clear the controlled search so it does not cover the chosen label.
  const handleChange = (...args) => {
    if (mode !== "multiple") {
      onSearch("");
    }
    onChange?.(...args);
  };

  const handleBlur = (...args) => {
    onSearch("");
    rest.onBlur?.(...args);
  };

  const { data: projectUsers, refetch } = useQuery({
    queryKey: ["projectUsers", projectId, 1, 1000],
    queryFn: () => fetchProjectUsers(projectId, 1, 1000),
    enabled: !!projectId,
  });

  const filteredUsers = (projectUsers?.data || []).filter((user) => {
    if (!roles || roles.length === 0) return true;
    return roles.includes(user.role);
  });

  const handleUserCreated = async (email) => {
    const result = await refetch();
    const newUser = result.data?.data?.find(
      (user) => user.email?.toLowerCase() === email?.toLowerCase(),
    );
    if (!newUser || !onChange) {
      return;
    }

    if (roles?.length && !roles.includes(newUser.role)) {
      return;
    }

    const userId = newUser.id || newUser._id;
    if (mode === "multiple") {
      const currentValues = Array.isArray(value) ? value : [];
      if (!currentValues.includes(userId)) {
        onChange([...currentValues, userId]);
      }
    } else {
      onChange(userId);
    }
    onSearch("");
  };

  const dropdownRender = canCreateUser
    ? (menu) => renderCreateUserFooter(menu, () => setShowCreateUserModal(true))
    : undefined;

  return (
    <>
      <Select
        showSearch
        optionFilterProp="children"
        filterOption={filterOption}
        mode={mode === null || mode === undefined ? undefined : mode}
        value={value}
        onChange={handleChange}
        disabled={disabled}
        searchValue={searchValue}
        onSearch={onSearch}
        placeholder={placeholder}
        className={className}
        allowClear={allowClear}
        getPopupContainer={() => document.body}
        options={filteredUsers.map((user) => ({
          value: user.id || user._id,
          label: `${user.username}${
            user.vendor_name ? ` (${user.vendor_name})` : ""
          }`,
        }))}
        dropdownRender={dropdownRender}
        {...rest}
        onBlur={handleBlur}
      />
      <CreateUserModal
        visible={showCreateUserModal}
        onClose={() => setShowCreateUserModal(false)}
        onUserCreated={handleUserCreated}
      />
    </>
  );
};

export default ProjectUserSelect;
